import React from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  DataTable, DownloadButton, MeterBar, Panel, SortHeader, nextSort, type DataTableColumn, type SortDirection,
} from '../../../../ui-kit';
import { formatValue } from '../../format';
import { measureFormat } from '../lookupFormat';
import type { LookupRankingBlock, LookupRankingRow } from '../../../../types/lookupDashboard';

/** `total` marks the Total row at the foot: no rank, not pickable, drawn bold. */
type RankedRow = LookupRankingRow & { rank: number; total?: boolean };

interface RankingPanelProps {
  block: LookupRankingBlock;
  loading?: boolean;
  onDownload: () => void;
  downloading?: boolean;
  /** Problem with this table's download, shown along its foot. */
  problem?: string | null;
  /**
   * On a block with a `filter`, picking a row's name filters the page to it —
   * the case manager — and picking it again clears it.
   */
  onPick?: (label: string) => void;
  /** The names picked now, drawn bold. */
  picked?: string[];
}

/**
 * One "top N by …" table — operators, broker companies, aircraft categories —
 * with Download, expand and its definitions.
 */
export default function RankingPanel({ block, loading, onDownload, downloading, problem, onPick, picked = [] }: RankingPanelProps) {
  // Clicking a heading re-sorts the rows; `#` keeps each row's rank by the
  // block's own order, so "3rd by legs" still reads after sorting by price.
  const [sort, setSort] = React.useState<{ key: string; direction: SortDirection } | null>(null);
  const ranked: RankedRow[] = block.rows.map((r, i) => ({ ...r, rank: i + 1 }));
  const sortValue = (r: RankedRow, key: string): string | number | null =>
    key === 'label' ? r.label.toLowerCase()
      : key === '__sub' ? (r.sub_label ?? '').toLowerCase()
        : key === '__share' ? r.share : r.values[key] ?? null;
  const sorted = sort
    ? [...ranked].sort((a, b) => {
        const x = sortValue(a, sort.key);
        const y = sortValue(b, sort.key);
        // Blanks last whichever way, so an unpriced operator never tops a price sort.
        if (x === null || y === null) return x === y ? a.rank - b.rank : x === null ? 1 : -1;
        const c = x < y ? -1 : x > y ? 1 : 0;
        return (sort.direction === 'asc' ? c : -c) || a.rank - b.rank;
      })
    : ranked;
  // The Total stays at the foot whatever the sort.
  const rows: RankedRow[] = block.total && sorted.length
    ? [...sorted, { label: 'Total', values: block.total, share: block.share_label ? 100 : null, rank: 0, total: true }]
    : sorted;
  const bold = (r: RankedRow, content: React.ReactNode) =>
    r.total ? <Box component="span" sx={{ fontWeight: 600 }}>{content}</Box> : content;
  const heading = (key: string, label: string, numeric = false) => (
    <SortHeader
      label={label}
      numeric={numeric}
      direction={sort?.key === key ? sort.direction : null}
      onClick={() => setSort((current) => nextSort(current, key))}
    />
  );

  const [lead, ...rest] = block.measures;
  const maxShare = Math.max(0, ...block.rows.map((r) => r.share ?? 0));
  const measureColumn = (m: LookupRankingBlock['measures'][number]): DataTableColumn<RankedRow> => ({
    key: m.key,
    header: heading(m.key, m.short_label ?? m.label, true),
    numeric: true,
    render: (r) => bold(r, formatValue(r.values[m.key], measureFormat(m.format))),
  });

  const columns: DataTableColumn<RankedRow>[] = [
    // Rank and row name stay put: a measure scrolled to is unreadable without
    // the name it belongs to.
    { key: 'rank', header: '#', width: 40, sticky: true, muted: true, render: (r) => (r.total ? null : r.rank) },
    {
      // Narrower beside a second label, so the figures still fit a half-width panel.
      key: 'label', header: heading('label', block.row_label), width: block.sub_row_label ? 150 : 220, sticky: true,
      wrap: !!block.sub_row_label,
      render: (r) => (r.total ? bold(r, r.label) : block.filter && onPick ? (
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => onPick(r.label)}
          aria-pressed={picked.includes(r.label)}
          sx={(t) => ({
            textAlign: 'left',
            color: picked.includes(r.label) ? t.tokens.text.accent : t.tokens.text.primary,
            fontWeight: picked.includes(r.label) ? 600 : 400,
          })}
        >
          {r.label}
        </Link>
      ) : r.label),
    },
    // A second label, e.g. the sub-category beside the category; frozen with it.
    ...(block.sub_row_label
      ? [{
          key: '__sub',
          header: heading('__sub', block.sub_row_label),
          width: 170,
          sticky: true,
          wrap: true,
          render: (r: RankedRow) => (r.total ? null : r.sub_label),
        }]
      : []),
    // The count, then its share beside it, then the averages.
    ...(lead ? [measureColumn(lead)] : []),
    ...(block.share_label
      ? [{
          key: '__share',
          header: heading('__share', block.share_label, true),
          numeric: true,
          muted: true,
          render: (r: RankedRow) => bold(r, formatValue(r.share, 'pct')),
        }]
      : []),
    ...rest.map(measureColumn),
    // Each row's share drawn, scaled so the largest fills the bar: the eye
    // ranks the rows without reading them. The Mix figure stays the number.
    ...(block.share_bar && block.share_label
      ? [{
          key: '__share_bar',
          // Named for screen readers; the bars speak for themselves on screen.
          header: <Box component="span" style={visuallyHidden}>{`${block.share_label} bar`}</Box>,
          name: `${block.share_label} bar`,
          // Shorter beside a second label column, so the whole table fits a
          // half-width panel without scrolling.
          width: block.sub_row_label ? 80 : 140,
          render: (r: RankedRow) => (r.total ? null : (
            <MeterBar
              value={maxShare ? ((r.share ?? 0) / maxShare) * 100 : 0}
              // Drawn against the largest row; heard as the row's own share.
              ariaValue={r.share ?? 0}
              tone={block.share_bar ?? 'brand'}
              ariaLabel={`${r.label}: ${formatValue(r.share, 'pct')} of ${block.measures[0]?.label ?? 'the total'}`}
            />
          )),
        }]
      : []),
  ];

  const definitions = block.measures.filter((m) => m.definition);
  const table = (fill: boolean) => (
    <DataTable
      ariaLabel={block.title ?? 'Ranking'}
      columns={columns}
      groups={[{ key: 'all', title: '', rows }]}
      getRowKey={(r) => (r.total ? '__total' : `${r.rank}-${r.label}`)}
      collapsibleGroups={false}
      fill={fill}
      emptyMessage="Nothing on record."
    />
  );

  const more = block.total_bands > block.rows.length;
  return (
    <Panel
      title={block.title}
      subtitle={block.filter && onPick ? `${block.subtitle ?? ''}${block.subtitle ? ' · ' : ''}pick a name to filter the page` : block.subtitle}
      info={
        definitions.length ? (
          <Stack spacing={0.75} sx={{ py: 0.25 }}>
            {definitions.map((m) => (
              <Typography key={m.key} variant="caption" sx={{ display: 'block' }}>
                <strong>{m.label}:</strong> {m.definition}
              </Typography>
            ))}
          </Stack>
        ) : undefined
      }
      actions={
        <DownloadButton
          formats={[{ key: 'xlsx', label: 'Excel (.xlsx)', onDownload }]}
          tooltip="Download this table as Excel, as shown"
          loading={downloading}
          disabled={loading || rows.length === 0}
        />
      }
      loading={loading}
      loadingHeight={280}
      flush
      footer={problem ?? (more ? `Top ${block.rows.length} of ${block.total_bands}.` : null)}
      expanded={{ content: table(true) }}
    >
      {table(false)}
    </Panel>
  );
}
